import { useEffect, useRef, useState } from "react";
import { IoCalendarOutline } from "react-icons/io5";

// Display format is dd/mm/yy (2-digit year, 20yy). Storage stays ISO (yyyy-mm-dd).
const isoToDisplay = (iso) => {
  if (!iso || iso.length < 10) return "";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return `${d}/${m}/${y.slice(-2)}`;
};

/** Validate that a (year, month, day) tuple corresponds to a real calendar date.
 *  E.g. 31/02/2027 → false, 30/04/2027 → true. */
const isRealDate = (y, m, d) => {
  const dt = new Date(Date.UTC(y, m - 1, d));
  return (
    dt.getUTCFullYear() === y &&
    dt.getUTCMonth() === m - 1 &&
    dt.getUTCDate() === d
  );
};

/** Last day of the given (year, month). Used when the user enters only mm/aa
 *  (some products carry only month+year on the label; the expiry is EOM). */
const lastDayOfMonth = (y, m) => new Date(Date.UTC(y, m, 0)).getUTCDate();

/** Accept: dd/mm/yy, dd/mm/yyyy, mm/yy, mm/yyyy. Returns:
 *  { iso, status: "empty"|"incomplete"|"invalid"|"ok" }.
 *  Month-only inputs auto-fill the last day of that month. */
const parseDisplay = (disp) => {
  const trimmed = (disp || "").trim();
  if (!trimmed) return { iso: "", status: "empty" };
  const dMonY2 = /^(\d{2})\/(\d{2})\/(\d{2})$/.exec(trimmed);   // dd/mm/yy
  const dMonY4 = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(trimmed);   // dd/mm/yyyy
  const monY2 = /^(\d{2})\/(\d{2})$/.exec(trimmed);             // mm/yy
  const monY4 = /^(\d{2})\/(\d{4})$/.exec(trimmed);             // mm/yyyy
  if (!dMonY2 && !dMonY4 && !monY2 && !monY4) return { iso: "", status: "incomplete" };

  let d, m, y;
  if (dMonY2 || dMonY4) {
    const M = dMonY2 || dMonY4;
    d = parseInt(M[1], 10);
    m = parseInt(M[2], 10);
    y = dMonY2 ? 2000 + parseInt(M[3], 10) : parseInt(M[3], 10);
  } else {
    const M = monY2 || monY4;
    m = parseInt(M[1], 10);
    y = monY2 ? 2000 + parseInt(M[2], 10) : parseInt(M[2], 10);
    if (m < 1 || m > 12) return { iso: "", status: "invalid" };
    d = lastDayOfMonth(y, m);
  }
  if (m < 1 || m > 12) return { iso: "", status: "invalid" };
  if (d < 1 || d > 31) return { iso: "", status: "invalid" };
  if (!isRealDate(y, m, d)) return { iso: "", status: "invalid" };
  const iso = `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  return { iso, status: "ok" };
};

// Auto-format while typing: allows both `dd/mm/yy` (6 digits) and `mm/yy` (4 digits).
// The user separates day from month with `/` — we insert it after every 2 digits.
const autoFormat = (raw) => {
  const digits = raw.replace(/\D/g, "").slice(0, 6);
  const parts = [];
  if (digits.length > 0) parts.push(digits.slice(0, 2));
  if (digits.length > 2) parts.push(digits.slice(2, 4));
  if (digits.length > 4) parts.push(digits.slice(4, 6));
  return parts.join("/");
};

export const DateInput = ({ value, onChange, "data-testid": testId, placeholder = "gg/mm/aa o mm/aa" }) => {
  const [text, setText] = useState(() => isoToDisplay(value));
  const [status, setStatus] = useState("empty");
  const hiddenRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    setText(isoToDisplay(value));
    setStatus(value ? "ok" : "empty");
  }, [value]);

  const handleTextChange = (e) => {
    const formatted = autoFormat(e.target.value);
    setText(formatted);
    const { iso, status: st } = parseDisplay(formatted);
    setStatus(st);
    if (st === "ok") onChange(iso);
    else if (formatted === "") onChange("");
    else onChange(""); // invalid or incomplete → clear stored value so parent knows it's not saved
  };

  /**
   * Select the segment (day / month / year) under the caret so that
   * clicking on it lets the user overwrite just that segment.
   * Positions in "dd/mm/yy":  day 0-2, separator 2, month 3-5, separator 5, year 6-8.
   */
  const selectSegmentAt = (pos) => {
    const el = inputRef.current;
    if (!el) return;
    if (text.length < 8) return;
    if (pos <= 2) el.setSelectionRange(0, 2);
    else if (pos <= 5) el.setSelectionRange(3, 5);
    else el.setSelectionRange(6, 8);
  };

  const handleClick = (e) => {
    const pos = e.target.selectionStart ?? 0;
    setTimeout(() => selectSegmentAt(pos), 0);
  };

  const handleFocus = (e) => {
    setTimeout(() => selectSegmentAt(e.target.selectionStart ?? 0), 0);
  };

  const openPicker = () => {
    if (!hiddenRef.current) return;
    if (typeof hiddenRef.current.showPicker === "function") {
      try {
        hiddenRef.current.showPicker();
        return;
      } catch (err) {
        if (process.env.NODE_ENV === "development") {
          console.debug("DateInput showPicker fallback:", err?.message || err);
        }
      }
    }
    hiddenRef.current.focus();
    hiddenRef.current.click();
  };

  const isInvalid = status === "invalid";

  return (
    <div className="tf-date-input-wrap">
      <input
        ref={inputRef}
        data-testid={testId}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        className={`tf-input tf-date-input${isInvalid ? " tf-input-error" : ""}`}
        placeholder={placeholder}
        value={text}
        onChange={handleTextChange}
        onClick={handleClick}
        onFocus={handleFocus}
        maxLength={8}
        aria-invalid={isInvalid || undefined}
      />
      <button
        type="button"
        className="tf-date-input-btn"
        onClick={openPicker}
        aria-label="Apri calendario"
        data-testid={testId ? `${testId}-picker` : undefined}
      >
        <IoCalendarOutline size={20} />
      </button>
      <input
        ref={hiddenRef}
        type="date"
        value={value || ""}
        onChange={(e) => {
          setText(isoToDisplay(e.target.value));
          setStatus(e.target.value ? "ok" : "empty");
          onChange(e.target.value);
        }}
        className="tf-date-input-hidden"
        tabIndex={-1}
        aria-hidden="true"
      />
      {isInvalid && (
        <div
          data-testid={testId ? `${testId}-invalid` : undefined}
          className="tf-input-error-msg"
          role="alert"
        >
          Data inesistente
        </div>
      )}
    </div>
  );
};
